import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Check, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { productsAPI } from '../services/api';
import { toast } from 'sonner';

const RulePreview = ({ rule }) => (
  <li className="text-xs text-gray-700">
    <span className="font-semibold">{rule.brand}</span>
    {rule.models?.length ? <> · {rule.models.join(', ')}</> : <span className="text-gray-400"> · tous les modèles</span>}
    {rule.fuels?.length ? <span className="text-gray-500"> · {rule.fuels.join(' / ')}</span> : null}
    {rule.engine_names?.length ? <span className="text-gray-500"> · {rule.engine_names.length} motorisation(s)</span> : null}
  </li>
);

// One sensitive product awaiting validation of inherited compatibilities
const PendingItem = ({ item, onDone, onEdit }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const p = item.compat_pending;
  const act = async (action) => {
    setBusy(action);
    try {
      await productsAPI.compatPendingAction(item.id, action);
      toast.success(action === 'accept' ? `Compatibilités fusionnées dans ${item.brand} ${item.ref}` : `Proposition refusée pour ${item.brand} ${item.ref}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    setBusy(null);
  };
  return (
    <li className="border rounded-lg p-3 bg-amber-50/40" data-testid={`compat-pending-${item.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <button type="button" onClick={() => onEdit?.(item.id)} className="font-medium text-gray-900 hover:text-orange-600 text-left truncate block max-w-md" data-testid={`compat-pending-name-${item.id}`}>{item.name}</button>
          <p className="text-xs text-gray-500">{item.brand} · {item.ref} · +{p.rules?.length || 0} marque(s)/règle(s), +{p.models_added || 0} modèle(s), +{p.fitments_added || 0} motorisation(s) · source : {(p.sources || []).map(s => `${s.brand || ''} ${s.ref || ''}`.trim()).slice(0, 3).join(', ')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-0.5" data-testid={`compat-pending-toggle-${item.id}`}>{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} prévisualiser</button>
          <Button size="sm" variant="outline" onClick={() => act('reject')} disabled={!!busy} data-testid={`compat-pending-reject-${item.id}`} className="border-red-300 text-red-700 hover:bg-red-50">{busy === 'reject' ? <Loader2 size={14} className="animate-spin" /> : <X size={14} className="mr-1" />} Refuser</Button>
          <Button size="sm" onClick={() => act('accept')} disabled={!!busy} data-testid={`compat-pending-accept-${item.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy === 'accept' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} className="mr-1" />} Accepter</Button>
        </div>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`compat-pending-preview-${item.id}`}>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">Règles proposées</p>
            {p.rules?.length ? <ul className="space-y-0.5">{p.rules.map((r, i) => <RulePreview key={i} rule={r} />)}</ul> : <p className="text-xs text-gray-400">Aucune nouvelle règle</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">Motorisations proposées ({p.fitments_added || 0})</p>
            {p.fitments?.length ? <ul className="space-y-0.5 max-h-40 overflow-y-auto">{p.fitments.slice(0, 40).map((f, i) => <li key={i} className="text-xs text-gray-700">{f.brand} {f.model} <span className="text-gray-500">{f.engine}{f.engine_years ? ` (${f.engine_years})` : ''}</span></li>)}{p.fitments.length > 40 && <li className="text-xs text-gray-400">… +{p.fitments.length - 40}</li>}</ul> : <p className="text-xs text-gray-400">Aucune</p>}
          </div>
        </div>
      )}
    </li>
  );
};

// Admin › Produits : review queue for products flagged "sensitive"
const CompatPendingList = ({ refreshKey, onChanged, onEdit }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => productsAPI.compatPending().then(d => setItems(d.items || [])).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!items?.length) return null;
  return (
    <div className="mt-4" data-testid="compat-pending-list">
      <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-2"><ShieldAlert size={14} /> {items.length} fiche(s) sensible(s) en attente de validation</h4>
      <ul className="space-y-2">{items.map(it => <PendingItem key={it.id} item={it} onEdit={onEdit} onDone={() => { load(); onChanged?.(); }} />)}</ul>
    </div>
  );
};

export default CompatPendingList;
